import { Instagram, MapPin, Phone } from "lucide-react";
import { motion } from "framer-motion";
import {
    AREA,
    INSTAGRAM_URL,
    NAV_LINKS,
    PHONE_DISPLAY,
    PHONE_TEL,
    WHATSAPP_URL,
    WhatsAppIcon,
} from "@/lib/site";

export const Contact = () => (
    <section id="contatti" className="scroll-mt-20 border-b-2 border-ink bg-cream">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:py-24">
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5 }}
                className="border-2 border-ink bg-white p-8 shadow-print sm:p-12"
            >
                <p className="mb-4 inline-block border-2 border-ink bg-cream px-3 py-1 text-xs font-bold uppercase tracking-widest">
                    Contatti
                </p>
                <h2 className="headline-display max-w-2xl text-4xl font-bold sm:text-5xl">
                    Il prossimo passo? Mostraci i tuoi fari.
                </h2>
                <p className="mt-6 max-w-2xl text-base font-medium leading-relaxed text-ink/80 md:text-lg">
                    Contattaci su WhatsApp e inviaci alcune fotografie dei fari
                    della tua auto: valutiamo le informazioni disponibili e ti
                    indichiamo come procedere. Il preventivo è gratuito.
                </p>
                <p className="mt-4 max-w-2xl text-sm font-medium leading-relaxed text-ink/70 md:text-base">
                    Operiamo a domicilio a Milano Est e nell'hinterland. Se la
                    tua zona è al limite dell'area servita, scrivici comunque:
                    valutiamo ogni richiesta caso per caso e ti rispondiamo con
                    chiarezza.
                </p>
                <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:flex-wrap">
                    <a
                        href={WHATSAPP_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="contact-whatsapp-btn"
                        className="flex items-center justify-center gap-2 border-2 border-ink bg-wa px-6 py-4 font-display text-lg font-semibold uppercase tracking-tight text-ink transition-[transform,box-shadow] duration-150 hover:-translate-y-1 hover:shadow-print"
                    >
                        <WhatsAppIcon className="h-5 w-5" />
                        Scrivici su WhatsApp
                    </a>
                    <a
                        href={PHONE_TEL}
                        data-testid="contact-phone-link"
                        className="flex items-center justify-center gap-2 border-2 border-ink bg-ink px-6 py-4 font-display text-lg font-semibold uppercase tracking-tight text-cream transition-[transform,box-shadow] duration-150 hover:-translate-y-1 hover:shadow-print"
                    >
                        <Phone className="h-5 w-5" aria-hidden="true" />
                        {PHONE_DISPLAY}
                    </a>
                </div>
                <div className="mt-8 flex flex-col gap-3 border-t-2 border-ink/15 pt-6 text-sm font-bold uppercase tracking-wider text-ink/70 sm:flex-row sm:gap-8">
                    <a
                        href={INSTAGRAM_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="contact-instagram-link"
                        className="flex items-center gap-2 hover:text-royal"
                    >
                        <Instagram className="h-4 w-4" aria-hidden="true" />
                        @clearlensmilano
                    </a>
                    <span className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" aria-hidden="true" />
                        Servizio a domicilio: {AREA}
                    </span>
                </div>
            </motion.div>
        </div>
    </section>
);

export const Footer = () => (
    <footer className="bg-navy py-12 text-cream navy-lines">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
                <div className="flex items-center gap-3">
                    <img
                        src="/assets/brand/logo.png"
                        alt="Logo CLEAR LENS MILANO"
                        className="h-12 w-12 border-2 border-cream object-cover"
                        width="48"
                        height="48"
                        loading="lazy"
                    />
                    <div>
                        <p className="font-display text-lg font-semibold uppercase tracking-tight">
                            Clear Lens Milano
                        </p>
                        <p className="text-xs font-bold uppercase tracking-widest text-cream/60">
                            Ripristino fari a domicilio
                        </p>
                    </div>
                </div>
                <nav
                    className="flex flex-wrap gap-x-6 gap-y-2"
                    aria-label="Navigazione footer"
                >
                    {NAV_LINKS.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            data-testid={`footer-link-${l.href.slice(1)}`}
                            className="text-xs font-bold uppercase tracking-widest text-cream/80 hover:text-wa"
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>
                <div className="flex gap-3">
                    <a
                        href={WHATSAPP_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="footer-whatsapp-link"
                        aria-label="Contattaci su WhatsApp"
                        className="flex h-11 w-11 items-center justify-center border-2 border-cream text-cream transition-colors hover:bg-wa hover:text-ink"
                    >
                        <WhatsAppIcon className="h-5 w-5" />
                    </a>
                    <a
                        href={INSTAGRAM_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="footer-instagram-link"
                        aria-label="Seguici su Instagram"
                        className="flex h-11 w-11 items-center justify-center border-2 border-cream text-cream transition-colors hover:bg-royal"
                    >
                        <Instagram className="h-5 w-5" aria-hidden="true" />
                    </a>
                    <a
                        href={PHONE_TEL}
                        data-testid="footer-phone-link"
                        aria-label={`Chiama ${PHONE_DISPLAY}`}
                        className="flex h-11 w-11 items-center justify-center border-2 border-cream text-cream transition-colors hover:bg-cream hover:text-navy"
                    >
                        <Phone className="h-5 w-5" aria-hidden="true" />
                    </a>
                </div>
            </div>
            <div className="mt-10 border-t border-cream/20 pt-6 text-xs font-semibold uppercase tracking-widest text-cream/50">
                <p className="max-w-3xl normal-case tracking-normal">
                    Clear Lens Milano — ripristino fari auto a domicilio a
                    Milano Est e Hinterland. Pulizia, carteggiatura, lucidatura
                    e protezione nanotecnologica dei fari in policarbonato, con
                    valutazione gratuita tramite fotografie su WhatsApp.
                </p>
                <p className="mt-3">
                    © {new Date().getFullYear()} Clear Lens Milano — {AREA} ·{" "}
                    {PHONE_DISPLAY}
                </p>
            </div>
        </div>
    </footer>
);

export const StickyWhatsApp = () => (
    <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="sticky-whatsapp-btn"
        aria-label="Apri la chat WhatsApp con CLEAR LENS MILANO"
        className="fixed bottom-5 right-5 z-50 flex h-14 w-14 items-center justify-center border-2 border-ink bg-wa text-ink shadow-print-sm transition-transform duration-150 hover:-translate-y-1 lg:hidden"
    >
        <WhatsAppIcon className="h-7 w-7" />
    </a>
);
